import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import SamsungPayMobileBox from '../../components/common/SamsungPayMobileBox';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {getLanguageName} from '../../utils/getLanguage';
import i18n from '../../i18n/i18n';
import {getPaymentClient, getPaymentCredentials, getTransactionResult, updateTransactionStatus} from '../../libs/GsmpiApi';
import {useDispatch, useSelector} from 'react-redux';
import {setData} from '../../data/paymentClientSlice';
import {
  MOBILE_ERROR_TYPE,
  PAYMENT_STATUS,
  POLLING_TIME,
  POST_MESSAGE_TYPE,
  TRANSACTION_STATUS,
  POST_MESSAGE_CONTENT_MESSAGE, MOBILE_INSTALL_TYPE, TRANSACTION_FINAL_STATUS
} from '../../common/constants';
import {postMessageToOpener} from '../../utils/postMessage';
import styles from '../../css/SamsungPayMobileBox.module.css';
import classNames from 'classnames/bind';
import {clearInterval, clearTimeout, setInterval, setTimeout} from 'worker-timers';
import debug from 'debug';

const cx = classNames.bind(styles);
const log = debug('app:MobileBridgePage');

function handleTransactionResult(result, navigate) {
  switch (result.status) {
  case TRANSACTION_STATUS.EXPIRED:
    navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
    break;
  case TRANSACTION_STATUS.CANCELED:
    navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
    break;
  case TRANSACTION_STATUS.ERRED:
    navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.OTHER_ERRORS}});
    break;
  case TRANSACTION_STATUS.APP_UPDATE_REQUIRED:
    navigate(`/gsmpi-web/mobile/${i18n.language}/install`, {state: {installType: MOBILE_INSTALL_TYPE.UPDATE_APP}});
    break;
  default:
    log(`result status : ${result.status}`);
  }
}

export default function MobileBridgePage() {
  const [statusUpdateCountDown, setStatusUpdateCountDown] = useState(null);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(0);
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get('clientId');
  const isFromQr = (queryParams.get('isFromQr') === "true");
  const {serviceId, transactionId, sourceOrigin, merchantName} = useSelector((state) => state.paymentClient);
  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(false);
  const abortControllersRef = useRef([]);
  const hasLaunchedRef = useRef(false);

  function handleConfirmedTransaction(refId) {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        postMessageToOpener({
          type: POST_MESSAGE_TYPE.PAYMENT_RESULT,
          content: {
            status: PAYMENT_STATUS.SUCCESS,
            data: result.data,
            notificationContent: {
              payment: {
                service: {
                  id: serviceId
                },
                reference: refId,
                status: '',
                provider: ''
              },
              merchant: {
                name: merchantName
              }
            }
          },
          sourceOrigin: sourceOrigin
        });

        self.close();
      })
      .catch(() => navigate(`/gsmpi-web/mobile/${i18n.language}/error`));
  }

  useEffect(() => {
    if (clientId) {
      getPaymentClient({clientId: clientId})
        .then((response) => {
          if (response.status === 200) {
            dispatch(setData({
              serviceId: response.data.serviceId,
              transactionId: response.data.transactionId,
              merchantName: response.data.merchantName,
              domains: response.data.domains,
              sourceOrigin: response.data.sourceOrigin
            }));
            return response.data.transactionId;
          }
        }).then((transactionId) => {
          getTransactionResult(transactionId)
            .then(result => {
              if (!isFromQr) {
                handleTransactionResult(result.data, navigate);
              }
            })
            .catch(() => {
              if (isFromQr) {
                window.close();
              } else {
                navigate(`/gsmpi-web/mobile/${i18n.language}/error`);
              }
            });
        }).then(() => {
          setStatusPollingCountDown(POLLING_TIME.MOBILE_POLL);
          setStatusUpdateCountDown(POLLING_TIME.MOBILE_POLL);
        })
        .catch(() => {
          if (isFromQr) {
            window.close();
          } else {
            navigate(`/gsmpi-web/mobile/${i18n.language}/error`);
          }
        });
    } else {
      if (isFromQr) {
        window.close();
      } else {
        navigate(`/gsmpi-web/mobile/${i18n.language}/error`);
      }
    }
  }, [clientId]);

  useEffect(() => {
    if (statusUpdateCountDown !== null) {
      if (statusUpdateCountDown > 0) {
        const timerInterval = setInterval(() => {
          setStatusUpdateCountDown(prevCountDown => prevCountDown > 0 ? prevCountDown - 1 : 0);
        }, 1000);

        return () => clearInterval(timerInterval);
      } else {
        if (!isFromQr) {
          const timedOutPayload = {
            payment: {
              status: TRANSACTION_STATUS.EXPIRED,
              reason: 'transactionExpired'
            },
            timestamp: new Date().getTime()
          };

          updateTransactionStatus(transactionId, timedOutPayload)
            .then(() => {
              navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
            })
            .catch((error) => {
              log(`failed to update transaction status >> ${error}`);
              navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
            });
        }
      }
    }
  }, [statusUpdateCountDown]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        if (!isFromQr && !window.navigator.onLine) {
          navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.INTERNET_CONNECTION_FAILURE}});
        }

        getTransactionResult(transactionId, true, abortSignal)
          .then(result => {
            abortControllersRef.current = abortControllersRef.current.filter(controller => controller !== abortController);

            if (isFromQr) {
              if (TRANSACTION_FINAL_STATUS.includes(result.data.status)) {
                clearInterval(pollingInterval);
                cancelPollingRequests();
                window.close();
              }
            } else {
              if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
                clearInterval(pollingInterval);
                cancelPollingRequests();
                handleConfirmedTransaction(result.data.refId);
              } else {
                handleTransactionResult(result.data, navigate);
              }
            }
          })
          .catch(error => {
            if (isFromQr) {
              if (error.response?.data?.resultCode) {
                clearInterval(pollingInterval);
                cancelPollingRequests();
                window.close();
              }
            } else {
              if (error.response.data.resultCode === 'OPM1N1042') {
                // device not supported
                navigate(`/gsmpi-web/mobile/${i18n.language}/error`);
              } else if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
                //transaction is expired
                navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
              } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
                //transaction is invalid
                navigate(`/gsmpi-web/mobile/${i18n.language}/error`);
              } else if (error.response.data.resultCode === 'OPM1N1046') {
                //app needs update
                navigate(`/gsmpi-web/mobile/${i18n.language}/install`, {state: {installType: MOBILE_INSTALL_TYPE.UPDATE_APP}});
              }
            }
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  useEffect(() => {
    if (!isDisabled) {
      return;
    }

    const unlock = () => setIsDisabled(false);

    const timer = setTimeout(unlock, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [isDisabled]);

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const isEdgeAndroid = () => {
    return /EdgA\//i.test(navigator.userAgent);
  };

  const getIntentUrl = () => {
    const intentUrl = `intent://launch?action=aboutsamsungpay&subAction=webpayment` +
        `&transactionId=${transactionId}&serviceId=${serviceId}` +
        `#Intent;scheme=samsungpay;package=com.samsung.android.spay;`;
    const fallbackUrl = encodeURIComponent(window.location.origin + `/gsmpi-web/mobile/${i18n.language}/install`);

    if (isEdgeAndroid()) {
      return intentUrl + "end";
    }

    return intentUrl + `S.browser_fallback_url=${fallbackUrl};end`;
  };

  const requestPaymentToApp = () => {
    window.location.href = getIntentUrl();
  };

  const handlePayRequestClick = () => {
    setIsDisabled(true);
    hasLaunchedRef.current = true;
    requestPaymentToApp();
  };

  const handleChangeLanguageClick = () => {
    if (isFromQr) {
      navigate(`/gsmpi-web/mobile/${i18n.language}/language${location.search}`, { replace: true });
    } else {
      navigate(`/gsmpi-web/mobile/${i18n.language}/language${location.search}`);
    }
  };

  const handleCloseButtonClick = () => {
    const timeOutPayload = {
      payment: {
        status: TRANSACTION_STATUS.CANCELED,
        reason: 'cancelledByUser'
      },
      timestamp: new Date().getTime()
    };

    if (isFromQr) {
      window.close();
    } else {
      updateTransactionStatus(transactionId, timeOutPayload)
        .then(() => {
          navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
        })
        .catch(() => {
          log('failed to update transaction status');

          navigate(`/gsmpi-web/mobile/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
        });
    }
  };

  useEffect(() => {
    const handleDiscontinuedTransaction = (e) => {
      //this will trigger once the page is about to be unloaded and the user hasn't clicked the 'Pay' button
      if (!e.persisted && !hasLaunchedRef.current && !isFromQr) {
        postMessageToOpener({
          type: POST_MESSAGE_TYPE.PAYMENT_RESULT,
          content: {
            status: PAYMENT_STATUS.FAIL,
            message: POST_MESSAGE_CONTENT_MESSAGE.USER_CANCEL
          },
          sourceOrigin: sourceOrigin
        });
      }
    };

    window.addEventListener('pagehide', handleDiscontinuedTransaction);

    return () => {
      window.removeEventListener('pagehide', handleDiscontinuedTransaction);
    };
  }, [sourceOrigin]);

  return (
    <SamsungPayMobileBox mobilePageType="main" closeButtonFunction={handleCloseButtonClick}>
      <div id="payment">
        <p className={cx('txt')}>
          <span id="merchantName">{t('pay.gmspi.mobile.main.paymentRequest').replace('%s', merchantName)}</span>
        </p>
        <div className={cx('btn-block')}>
          <button type="button" className={cx('btn')} onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            } else {
              handlePayRequestClick();
            }
          }}>
            {t('pay.gsmpi.mobile.main.button1')}
          </button>
        </div>
        <div className={cx('change-region')}>
          <button type="button" onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            } else {
              handleChangeLanguageClick();
            }
          }}>
            <span className={cx('mpi-regionbtn-text')}>{getLanguageName(i18n.language)}</span>
          </button>
        </div>
      </div>
    </SamsungPayMobileBox>
  );
}